import { isArchiveMemberSearchablePath, toProvenanceLookupPath } from '@/shared/functions';

export type CanonicalObjectRef = {
  bucket: string | null;
  key: string;
  kind: 'object' | 'archive_member';
};

const PROXY_PREFIX = '/api/proxy/';
const S3_HOST = /^(?:(.+)\.)?s3[.-](?:[a-z0-9-]+\.)?amazonaws\.com(?:\.cn)?$/i;

function decodeOnce(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

function fromKey(bucket: string | null, key: string): CanonicalObjectRef | null {
  if (!key) return null;
  return {
    bucket,
    key,
    kind: isArchiveMemberSearchablePath(key) ? 'archive_member' : 'object',
  };
}

function fromRawKey(raw: string): CanonicalObjectRef | null {
  const key = /^\/?files\//.test(raw) ? raw.replace(/^\/+/, '') : toProvenanceLookupPath(raw);
  return fromKey(null, key);
}

function fromProxyPath(rest: string): CanonicalObjectRef | null {
  const decoded = decodeOnce(rest.split('?')[0] ?? '');
  return decoded === null ? null : fromRawKey(decoded);
}

function fromHttpUrl(raw: string): CanonicalObjectRef | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.pathname.startsWith(PROXY_PREFIX)) {
    return fromProxyPath(url.pathname.slice(PROXY_PREFIX.length));
  }
  const host = S3_HOST.exec(url.hostname);
  if (!host) return null;
  const segments = url.pathname.replace(/^\/+/, '').split('/').map(decodeOnce);
  if (segments.some((segment) => segment === null)) return null;
  const parts = segments as string[];
  const virtualHostedBucket = host[1] ?? null;
  if (virtualHostedBucket) return fromKey(virtualHostedBucket, parts.join('/'));
  const [bucket, ...key] = parts;
  return bucket ? fromKey(bucket, key.join('/')) : null;
}

/**
 * One exact storage identity for every representation the app uses for an S3 object.
 * Case is kept, a raw key is never percent-decoded, and a host that is not S3 gives null.
 */
export function canonicalObjectRef(input: string | null | undefined): CanonicalObjectRef | null {
  const ref = (input ?? '').trim();
  if (!ref) return null;
  if (ref.startsWith('s3://')) {
    const rest = ref.slice('s3://'.length);
    const slash = rest.indexOf('/');
    if (slash <= 0) return null;
    return fromKey(rest.slice(0, slash), rest.slice(slash + 1));
  }
  if (/^https?:\/\//i.test(ref)) return fromHttpUrl(ref);
  if (ref.startsWith(PROXY_PREFIX)) return fromProxyPath(ref.slice(PROXY_PREFIX.length));
  return fromRawKey(ref);
}

/** True only when both refs name the same object: same key, and same bucket when both carry one. */
export function isSameObjectRef(
  a: CanonicalObjectRef | null,
  b: CanonicalObjectRef | null,
): boolean {
  if (!a || !b || a.key !== b.key) return false;
  return a.bucket === null || b.bucket === null || a.bucket === b.bucket;
}
